import { useMemo, useState, type PropsWithChildren } from "react";

import clsx from "clsx";
import { compact, flatten, isEmpty } from "lodash";
import { type Dictionary } from "ts-essentials";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { Loading } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  FETCH_BENCHMARK_SETTINGS,
  FETCH_USABLE_BENCHMARK_ATTRIBUTION_WINDOWS,
} from "@gql/benchmarks";
import { useNorthbeamQuery } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  BenchmarkAttributionModelEnum,
  BenchmarkAttributionWindowEnum,
  BenchmarkPlatformEnum,
  BenchmarkTypeEnum,
  type FetchBenchmarkSettings,
  type FetchBenchmarkSettingsVariables,
  type FetchBenchmarkSettings_me_benchmarks_benchmarkSettings_customBenchmarks,
  type FetchUsableBenchmarkAttributionWindows,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { emptyText } from "@north-beam/nb-common";
import { IconContainer } from "@pages/objects/utils";
import { IconButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";
import { Switch } from "@shared/switch";

import {
  BenchmarkModalProvider,
  useHasCompleteDatasetByAttrWindowAtom,
  useUsableBenchmarkWindowsAtom,
  type BenchmarkModalNavigationRoute,
  type HasCompleteDatasetByAttrWindow,
} from "../atoms";
import {
  BenchmarkAttributionWindowOptions,
  BenchmarkAttributionWindowValues,
  BenchmarkPlatformFriendlyNamesSalesPageV3,
  PrettyBenchmarkAttributionModelNames,
} from "../constants";
import {
  useBenchmarkAttributionWindow,
  useBenchmarks,
  useGetBenchmarkPlatformsToUse,
  useShowBenchmarks,
  useShowNBCalculatedBenchmarks,
} from "../hooks";
import { getPrettyBenchmark } from "../utils";

import { Alert } from "./alert";
import { BenchmarkAvailabilityCard } from "./benchmark-availability-card";
import { BenchmarkCard } from "./benchmark-card";
import { BenchmarkModal, CustomBenchmarksFlowModal } from "./benchmark-modal";

const LoadingBenchmarks = () => (
  <div className="relative">
    <Loading />
    <PlaceholderBenchmarks />
  </div>
);

const PlaceholderBenchmarks = () => {
  return (
    <div className="mt-5 grid gap-8 grid-cols-3 w-full relative">
      <BenchmarkCard
        title={
          BenchmarkPlatformFriendlyNamesSalesPageV3[
            BenchmarkPlatformEnum.adwords
          ]
        }
        platform={BenchmarkPlatformEnum.adwords}
        cac={null}
        roas={null}
        loading={false}
        ignoreNoData
        target={emptyText}
        benchmarkType={BenchmarkTypeEnum.CAC}
        attributionWindow={BenchmarkAttributionWindowEnum.THIRTY_DAYS}
      />
      <BenchmarkCard
        title={
          BenchmarkPlatformFriendlyNamesSalesPageV3[
            BenchmarkPlatformEnum.facebook
          ]
        }
        platform={BenchmarkPlatformEnum.facebook}
        cac={null}
        roas={null}
        loading={false}
        ignoreNoData
        target={emptyText}
        benchmarkType={BenchmarkTypeEnum.CAC}
        attributionWindow={BenchmarkAttributionWindowEnum.THIRTY_DAYS}
      />
      <BenchmarkCard
        title={
          BenchmarkPlatformFriendlyNamesSalesPageV3[
            BenchmarkPlatformEnum.youtube
          ]
        }
        platform={BenchmarkPlatformEnum.youtube}
        cac={null}
        roas={null}
        loading={false}
        ignoreNoData
        target={emptyText}
        benchmarkType={BenchmarkTypeEnum.CAC}
        attributionWindow={BenchmarkAttributionWindowEnum.THIRTY_DAYS}
      />
    </div>
  );
};

export const BenchmarkSettingSectionInnerWrapper = ({
  children,
}: PropsWithChildren<unknown>) => <div className="w-full">{children}</div>;

export const NoBenchmarkSettingsSection = ({
  openModal,
  attributionModel,
  currentAttributionWindow,
  setCurrentAttributionWindow,
  available,
}: {
  available: boolean;
  openModal: () => void;
  attributionModel: BenchmarkAttributionModelEnum;
  currentAttributionWindow: BenchmarkAttributionWindowEnum;
  setCurrentAttributionWindow: (value: BenchmarkAttributionWindowEnum) => void;
}) => {
  const { user } = useUser();

  return (
    <BenchmarkSettingSectionInnerWrapper>
      <div className="w-40 my-8">
        <div className="text-xs font-bold mb-1">NO BENCHMARK SET FOR</div>
        <div>
          <Select
            className="h-9"
            options={BenchmarkAttributionWindowOptions}
            value={currentAttributionWindow}
            onChange={(option) => {
              option && setCurrentAttributionWindow(option);
              prettyLogEvent.onSelect({
                page: Pages.SALES,
                domain: "Benchmarking",
                additionalActionInfo: "section-attributionWindow",
                data: { attributionModel },
                user,
              });
            }}
          />
        </div>
      </div>
      <BenchmarkAvailabilityCard available={available} onContinue={openModal} />
    </BenchmarkSettingSectionInnerWrapper>
  );
};

export const BenchmarkSettingSectionInner = ({
  loading,
  attributionModel,
  openModal,
  onEdit,
  currentAttributionWindow,
  loading: outerSectionLoading,
  setCurrentAttributionWindow,
}: {
  loading: boolean;
  attributionModel: BenchmarkAttributionModelEnum;
  openModal: () => void;
  onEdit: () => void;
  currentAttributionWindow: BenchmarkAttributionWindowEnum;
  setCurrentAttributionWindow: (
    attributionWindow: BenchmarkAttributionWindowEnum,
  ) => void;
}) => {
  const [showNBCalculatedBenchmarks, setShowNBCalculatedBenchmarks] =
    useShowNBCalculatedBenchmarks();
  const platformsToUse = useGetBenchmarkPlatformsToUse();
  const { user } = useUser();
  const [queryParamAttributionModel] = useQueryParam(
    "attributionModel",
    StringParam,
    { updateType: "pushIn" },
  );

  const memoizedAttributionModels = useMemo(() => {
    return [attributionModel];
  }, [attributionModel]);

  const [usabledAttributionWindows] = useUsableBenchmarkWindowsAtom();
  const [hasCompleteDatasetByAttrWindow] =
    useHasCompleteDatasetByAttrWindowAtom();
  const available = Boolean(
    usabledAttributionWindows?.has(currentAttributionWindow),
  );

  const {
    benchmarkSettings,
    benchmarkSettingsLoading,
    platformBenchmarksLoading,
    orderedData,
    customBenchmarks,
  } = useBenchmarks(
    attributionModel,
    currentAttributionWindow,
    platformsToUse,
    memoizedAttributionModels,
  );

  const customBenmarksByPlatform = useMemo(() => {
    if (!customBenchmarks) {
      return undefined;
    }
    /* eslint-disable-next-line max-len */
    const benchmarksByPlatform: Dictionary<FetchBenchmarkSettings_me_benchmarks_benchmarkSettings_customBenchmarks> =
      {};
    customBenchmarks.forEach((customBenchmark) => {
      if (customBenchmark.attributionModel === attributionModel) {
        benchmarksByPlatform[customBenchmark.platform] = customBenchmark;
      }
    });
    return benchmarksByPlatform;
  }, [attributionModel, customBenchmarks]);

  if (
    benchmarkSettingsLoading ||
    platformBenchmarksLoading ||
    outerSectionLoading
  ) {
    return <LoadingBenchmarks />;
  }

  const isNotFullDataset =
    !hasCompleteDatasetByAttrWindow[currentAttributionWindow];
  if (!available || isEmpty(benchmarkSettings)) {
    return (
      <NoBenchmarkSettingsSection
        available={available}
        openModal={openModal}
        attributionModel={attributionModel}
        currentAttributionWindow={currentAttributionWindow}
        setCurrentAttributionWindow={setCurrentAttributionWindow}
      />
    );
  }

  return (
    <BenchmarkSettingSectionInnerWrapper>
      <div className="flex gap-3 mt-9 mb-7">
        <div className="w-32">
          <div className="text-xs font-bold mb-1">BENCHMARK</div>
          <div className="text-sm bg-interstellar-3 rounded px-4 py-2">
            1 day
          </div>
        </div>
        <div className="w-32">
          <div className="text-xs font-bold mb-1">ATTRIBUTION MODEL</div>
          <div className="text-sm bg-interstellar-3 rounded px-4 py-2">
            {PrettyBenchmarkAttributionModelNames[attributionModel]}
          </div>
        </div>
        <div className="w-32">
          <div className="text-xs font-bold mb-1">
            TARGET RANGE
            <GenericTooltip
              content={
                <div className="w-96">
                  One-day benchmarks are calculated by taking the median
                  percentage lift in revenue from a one-day attribution window
                  to a thirty, sixty, or ninety-day attribution window.
                </div>
              }
            />
          </div>
          <div>
            <Select
              className="h-9"
              options={BenchmarkAttributionWindowOptions}
              value={currentAttributionWindow}
              onChange={(option) => {
                option && setCurrentAttributionWindow(option);
                prettyLogEvent.onSelect({
                  page: Pages.SALES,
                  domain: "Benchmarking",
                  additionalActionInfo: "section-attributionWindow",
                  data: { attributionModel },
                  user,
                });
              }}
            />
          </div>
        </div>
        <div className="flex-1" />
        <div className="self-end">
          <IconButton
            className="flex wrap items-center uppercase font-bold mb-[1px]"
            variant="secondary"
            onClick={() => {
              onEdit();
              prettyLogEvent.onClick({
                page: Pages.SALES,
                domain: "Benchmarking",
                additionalActionInfo: "openBenchmarkingModal",
                data: { attributionModel },
                user,
              });
            }}
            leftIcon={<i className="fa-regular fa-sharp fa-pencil mr-1" />}
            loading={loading}
          >
            Edit targets for &apos;
            {
              PrettyBenchmarkAttributionModelNames[
                QueryParamAttributionWindowToAttrModelMapping[
                  queryParamAttributionModel ?? ""
                ]
              ]
            }
            &apos;
          </IconButton>
        </div>
      </div>
      <div className="flex flex-col mt-5">
        {customBenchmarks && (
          <div className="self-end text-sm flex items-center mb-4">
            <div className="mr-2">See Northbeam calculated benchmarks</div>
            <Switch
              size="small"
              value={showNBCalculatedBenchmarks}
              onChange={() =>
                setShowNBCalculatedBenchmarks(!showNBCalculatedBenchmarks)
              }
            />
          </div>
        )}
        <div className="mt-2 grid gap-7 grid-cols-3 w-full relative">
          {orderedData.map((platformBenchmark) => {
            const {
              cac,
              roas,
              platform,
              attributionModel,
              target,
              benchmarkType,
            } = platformBenchmark;
            return (
              <BenchmarkCard
                attributionWindow={currentAttributionWindow}
                key={`${platform}-${attributionModel}`}
                title={BenchmarkPlatformFriendlyNamesSalesPageV3[platform]}
                platform={platform}
                target={getPrettyBenchmark(benchmarkType, target, user)}
                benchmarkType={benchmarkType}
                cac={customBenmarksByPlatform?.[platform].cac ?? cac}
                roas={customBenmarksByPlatform?.[platform].roas ?? roas}
                loading={platformBenchmarksLoading}
                nbCalculatedBenchmarks={platformBenchmark}
                showNbCalculatedBenchmarks={Boolean(
                  customBenchmarks && showNBCalculatedBenchmarks,
                )}
              />
            );
          })}
        </div>
        {isNotFullDataset && (
          <div className="font-light italic mt-3 text-sm ml-auto">
            * Missing complete dataset
          </div>
        )}
      </div>
    </BenchmarkSettingSectionInnerWrapper>
  );
};

export const QueryParamAttributionWindowToAttrModelMapping: {
  [key: string]: BenchmarkAttributionModelEnum;
} = {
  northbeam_custom: BenchmarkAttributionModelEnum.CLICKS_ONLY,
  northbeam_custom__va: BenchmarkAttributionModelEnum.CLICKS_AND_VIEWS,
};

const BenchmarksHeader = ({
  attributionModel,
  showSubheader,
}: {
  attributionModel: BenchmarkAttributionModelEnum;
  showSubheader: boolean;
}) => {
  return (
    <div>
      <span className="mr-3 font-bold">
        {PrettyBenchmarkAttributionModelNames[attributionModel]} 1 Day Media
        Buying Benchmark{" "}
        <GenericTooltip
          content={
            <div className="w-[25rem]">
              One-day media buying benchmarks are directional targets for daily
              optimization calculated by measuring the incremental lift in
              revenue associated with any single day&apos;s spend from a one-day
              attribution window to a thirty, sixty, or ninety day attribution
              window.
            </div>
          }
        />
      </span>
      {showSubheader && (
        <div className="text-sm mt-1">
          Turn CAC and ROAS goals into actionable 1 day targets for your media
          buying team.
          <a
            href="https://docs.northbeam.io/docs/sales-page#benchmarking-tool"
            target="_blank"
            rel="noreferrer"
            className="ml-2"
          >
            Learn more
          </a>
        </div>
      )}
    </div>
  );
};

const BenchmarksAvailable = ({
  onEdit,
  collapsed,
  setCollapsed,
  loading,
  attributionModel,
  currentAttributionWindow,
  hasBenchmarkSettings,
}: {
  hasBenchmarkSettings: boolean;
  onEdit: () => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  loading: boolean;
  attributionModel: BenchmarkAttributionModelEnum;
  currentAttributionWindow: BenchmarkAttributionWindowEnum;
}) => {
  const [usabledAttributionWindows] = useUsableBenchmarkWindowsAtom();
  const { user } = useUser();

  const available = Boolean(
    usabledAttributionWindows?.has(currentAttributionWindow),
  );
  return (
    <div className="flex wrap items-center">
      <BenchmarksHeader
        attributionModel={attributionModel}
        showSubheader={true}
      />
      <div className="ml-auto flex items-center">
        {!hasBenchmarkSettings && !loading && (
          <div className="flex bg-cosmic-4 text-primary w-fit px-3 py-2 rounded-lg mr-2">
            <IconContainer sizeInEM={1.5} style={{ marginRight: "0.3em" }}>
              <i
                className={clsx("cursor-pointer", {
                  "fa-regular fa-sharp fa-lock": !available,
                  "fa-regular fa-sharp fa-unlock": available,
                })}
              />
            </IconContainer>
            <div>
              {available ? "Now available" : "Not available"}{" "}
              <GenericTooltip
                content={
                  <div className="w-96">
                    {available
                      ? `Click 'Edit ${PrettyBenchmarkAttributionModelNames[attributionModel]}' button to set up your benchmarks`
                      : `Benchmarks require statistically significant data in order to be accurately calculated. You will need consistent spending and revenue attribution for a given platform, which can typically be gathered from ${
                          BenchmarkAttributionWindowValues[
                            currentAttributionWindow
                          ] * 2
                        } days of use.`}
                  </div>
                }
              />
            </div>
          </div>
        )}
        <IconButton
          onClick={() => {
            setCollapsed(!collapsed);

            prettyLogEvent.onClick({
              user,
              page: Pages.SALES,
              domain: "Benchmarking",
              additionalActionInfo: !collapsed ? "opened" : "closed",
            });
          }}
          size="lg"
          className="py-0"
        >
          {renderChevronAnimation(!collapsed)}
        </IconButton>
      </div>
    </div>
  );
};

const NoBenchmarksAvailable = ({
  attributionModel,
}: {
  attributionModel: BenchmarkAttributionModelEnum;
}) => {
  return (
    <div>
      <BenchmarksHeader
        attributionModel={attributionModel}
        showSubheader={false}
      />
      <div className="mt-2">
        <Alert>
          Only supported for “Clicks only” and “Clicks + Modeled Views”
          attribution models.
          <a
            href="https://docs.northbeam.io/docs/sales-page#benchmarking-tool"
            target="_blank"
            rel="noreferrer"
            className="ml-4"
          >
            Learn more
          </a>
        </Alert>
      </div>
    </div>
  );
};

export const BenchmarkSettings = () => {
  const { user } = useUser();
  const [queryParamAttributionModel] = useQueryParam(
    "attributionModel",
    withDefault(StringParam, user.getDefaultAttributionModel("")),
    { updateType: "pushIn" },
  );

  const attributionModel =
    QueryParamAttributionWindowToAttrModelMapping[
      queryParamAttributionModel ?? ""
    ];

  if (!attributionModel) {
    return (
      <div className="relative p-4 mb-8 rounded-lg border border-gray min-h-[3rem] bg-white">
        <NoBenchmarksAvailable attributionModel={attributionModel} />
      </div>
    );
  }
  return <BenchmarkSettingSection attributionModel={attributionModel} />;
};

export const BenchmarkSettingSection = ({
  attributionModel,
}: {
  attributionModel: BenchmarkAttributionModelEnum;
}) => {
  const [, setUsableBenchmarkWindows] = useUsableBenchmarkWindowsAtom();
  const [, setHasCompleteDatasetByAttrWindow] =
    useHasCompleteDatasetByAttrWindowAtom();
  const [collapsed, setCollapsed] = useShowBenchmarks();
  const [modalIsOpen, setModalOpen] = useState(false);

  const [initialModalNavigation, setInitialModalNavigation] =
    useState<BenchmarkModalNavigationRoute | null>(null);

  const [currentAttributionWindow, setCurrentAttributionWindow] =
    useBenchmarkAttributionWindow();

  const {
    data: currentBenchmarkSettings,
    loading: loadingCurrentBenchmarksSettings,
  } = useNorthbeamQuery<
    FetchBenchmarkSettings,
    FetchBenchmarkSettingsVariables
  >(FETCH_BENCHMARK_SETTINGS, {
    variables: {
      attributionWindow: currentAttributionWindow,
      attributionModel,
    },
  });

  const customBenchmarks = compact(
    flatten(
      currentBenchmarkSettings?.me.benchmarks.benchmarkSettings?.map(
        (setting) => setting.customBenchmarks,
      ) || [],
    ),
  );

  const { loading: loadingUsableAttributionWindows } =
    useNorthbeamQuery<FetchUsableBenchmarkAttributionWindows>(
      FETCH_USABLE_BENCHMARK_ATTRIBUTION_WINDOWS,
      {
        variables: {
          attributionModel,
        },
        onCompleted(data) {
          const hasCompleteDataset: HasCompleteDatasetByAttrWindow = {};
          const usableAttributionWindows =
            data.me.benchmarks.usableAttributionWindows.map((attrWindow) => {
              // during the mapping process, add to the `hasCompleteDataset`
              hasCompleteDataset[attrWindow.attributionWindow] =
                attrWindow.hasCompleteDataset;
              return attrWindow.attributionWindow;
            });

          setHasCompleteDatasetByAttrWindow(hasCompleteDataset);
          setUsableBenchmarkWindows(new Set(usableAttributionWindows));
        },
      },
    );

  const loading =
    loadingUsableAttributionWindows || loadingCurrentBenchmarksSettings;

  return (
    <div className="relative p-4 rounded-lg border border-gray min-h-[3rem] bg-white">
      {modalIsOpen && (
        <BenchmarkModalProvider>
          {initialModalNavigation === "review-with-custom-benchmarks" ? (
            <CustomBenchmarksFlowModal
              closeModal={() => setModalOpen(false)}
              initialModalNavigation={initialModalNavigation}
              setInitialModalNavigation={setInitialModalNavigation}
              attributionModel={attributionModel}
            />
          ) : (
            <BenchmarkModal
              closeModal={() => setModalOpen(false)}
              attributionModel={attributionModel}
            />
          )}
        </BenchmarkModalProvider>
      )}
      <BenchmarksAvailable
        onEdit={() => {
          if (customBenchmarks.length > 0) {
            setInitialModalNavigation("review-with-custom-benchmarks");
          } else {
            setInitialModalNavigation(null);
          }
          setModalOpen(true);
        }}
        hasBenchmarkSettings={Boolean(
          currentBenchmarkSettings?.me.benchmarks.benchmarkSettings,
        )}
        currentAttributionWindow={currentAttributionWindow}
        loading={loading}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        attributionModel={attributionModel}
      />
      {!collapsed && (
        <BenchmarkSettingSectionInner
          attributionModel={attributionModel}
          loading={loading}
          openModal={() => setModalOpen(true)}
          onEdit={() => {
            if (customBenchmarks.length > 0) {
              setInitialModalNavigation("review-with-custom-benchmarks");
            } else {
              setInitialModalNavigation(null);
            }
            setModalOpen(true);
          }}
          setCurrentAttributionWindow={setCurrentAttributionWindow}
          currentAttributionWindow={currentAttributionWindow}
        />
      )}
    </div>
  );
};
